// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// map.ts — one hand-authored map, 12 columns by 16 rows, and the arithmetic that reads it.
//
// It is 180°-ROTATIONALLY symmetric about the centre of the board, not mirror-symmetric. Point
// symmetry means seat 0's left flank maps onto seat 1's right flank, so the two seats are exactly
// equivalent and no handedness advantage can exist — and it also means the renderer can show each
// player the board from their own end simply by rotating it, with no second map and no chance of the
// two peers looking at different geometry. tests/rules.test.ts asserts the symmetry tile by tile;
// tests/balance.test.ts asserts that the bot playing itself finds no seat advantage in the result.
//
// TWO PLACEMENTS DO THE HEAVY LIFTING:
//
//  1. The GATE RIDGES sit directly in front of each Forge, so the straight column between the two
//     bases is blocked. Every attack on a base has to funnel through a two-tile gate at column 4 or
//     column 7. Artillery cannot shell a Forge down the middle; it has to win the gate first.
//  2. The SCRUB COLUMNS run the length of both flanks. Scrub is passable and opaque, so a long-range
//     unit is blind in it. That single fact inverts the counter triangle by lane: in the open waist
//     an Arbalest deletes an approaching Harrier before contact, and three tiles away in the scrub
//     the same Harrier arrives alive and kills it. A unit's counter becomes a property of where it
//     is standing, which no stat block encodes.
//
// The waist (rows 5-6 and 9-10) is pinched to eight passable tiles by the corner ridges, and the
// four central seams sit inside it — the money is exactly where the map is narrowest.

export const COLS = 12;
export const ROWS = 16;
export const TILES = COLS * ROWS;

/**
 * One tile = 255 sub-units, and the ODD NUMBER IS LOAD-BEARING.
 *
 * Every rule in the game asks "which tile is this unit in", which is `floor(x / SUB)`, and the map
 * is 180°-rotationally symmetric, so that answer has to survive the reflection `x → MIRROR_X − x`.
 * It only does when `MIRROR_X ≡ −1 (mod SUB)`, which forces the reflection axis onto an odd total
 * width — and a tile CENTRE only reflects onto a tile centre when the resulting half-offset is a
 * whole number, which forces SUB itself to be odd.
 *
 * With SUB = 256 the arithmetic is off by exactly one sub-unit for any position sitting on a tile
 * boundary, which sounds like nothing and is not: a unit standing on the line is read as being one
 * tile further north than its opposite number is read as being south, so it captures a seam a tick
 * earlier, and the seat that happens to arrive on a boundary wins the economy. That was the last of
 * five separate symmetry bugs in this file and by far the hardest to see.
 */
export const SUB = 255;
/** The centre of a tile, as an offset. (SUB−1)/2 — a whole number only because SUB is odd. */
export const HALF = (SUB - 1) / 2;
/** Reflect a sub-unit coordinate. `floor(x/SUB) + floor((MIRROR−x)/SUB)` is constant for all x. */
export const MIRROR_X = COLS * SUB - 1;
export const MIRROR_Y = ROWS * SUB - 1;

export const enum Terrain {
  Open = 0,
  Ridge = 1,
  Scrub = 2,
  Forge = 3,
}

/**
 * `.` open · `#` ridge · `,` scrub · `S` home seam (open ground, pre-claimed) · `C` central seam
 * (open ground, neutral) · `F` seat 1's Forge · `f` seat 0's Forge.
 *
 * Seat 0 is the SOUTH end (rows 14-15) and is the local player's seat in solo play.
 */
const SOURCE = [
  '.....FF.....',
  '.....FF.....',
  '.S,,....,,S.',
  '..,,.##.,,..',
  '.....##.....',
  '##........##',
  '#...C..C...#',
  '..,......,..',
  '..,......,..',
  '#...C..C...#',
  '##........##',
  '.....##.....',
  '..,,.##.,,..',
  '.S,,....,,S.',
  '.....ff.....',
  '.....ff.....',
];

export interface SeamSite {
  tile: number;
  /** 0 or 1 for a pre-claimed home seam, 2 for neutral. */
  owner: number;
}

export interface MapData {
  terrain: Uint8Array;
  /** Which seat owns each Forge tile, or 2 for "not a Forge". */
  forgeOf: Uint8Array;
  /** Move cost per tile, in percent of a full step: open 100, scrub 167. */
  cost: Uint8Array;
  /** Static line-of-fire blockers: ridge, scrub and Forge. Wrecks and Anvils add to this per tick. */
  opaque: Uint8Array;
  seams: SeamSite[];
  /** The 2x2 block of each side's Forge, seat-indexed. */
  forgeTiles: number[][];
  /** Where each side's two opening scouts stand. */
  openings: number[][];
  /** lastbatch's six pre-deployed bodies, as [unitType, tile] per seat. */
  deployment: Array<Array<[number, number]>>;
  /** theline's Foundry block, rows 7-8 x cols 4-7. */
  foundryTiles: number[];
}

export const idx = (c: number, r: number): number => r * COLS + c;
export const colOf = (t: number): number => t % COLS;
export const rowOf = (t: number): number => (t / COLS) | 0;
/** The 180° image of a tile. Used by the renderer to show seat 1 the board from its own end. */
export const flipTile = (t: number): number => idx(COLS - 1 - colOf(t), ROWS - 1 - rowOf(t));

let cached: MapData | null = null;

export function buildMap(): MapData {
  if (cached) return cached;

  const terrain = new Uint8Array(TILES);
  const forgeOf = new Uint8Array(TILES).fill(2);
  const cost = new Uint8Array(TILES).fill(100);
  const opaque = new Uint8Array(TILES);
  const seams: SeamSite[] = [];
  const forgeTiles: number[][] = [[], []];

  for (let r = 0; r < ROWS; r++) {
    const line = SOURCE[r];
    for (let c = 0; c < COLS; c++) {
      const t = idx(c, r);
      const ch = line[c];
      if (ch === '#') {
        terrain[t] = Terrain.Ridge;
        opaque[t] = 1;
        cost[t] = 0;
      } else if (ch === ',') {
        terrain[t] = Terrain.Scrub;
        opaque[t] = 1;
        cost[t] = 167;
      } else if (ch === 'F' || ch === 'f') {
        const seat = ch === 'f' ? 0 : 1;
        terrain[t] = Terrain.Forge;
        forgeOf[t] = seat;
        opaque[t] = 1;
        forgeTiles[seat].push(t);
      } else if (ch === 'S') {
        // A home seam belongs to whichever end of the board it sits on.
        seams.push({ tile: t, owner: r < ROWS / 2 ? 1 : 0 });
      } else if (ch === 'C') {
        seams.push({ tile: t, owner: 2 });
      }
    }
  }

  // Seams are listed in tile order so both peers iterate them identically. Sorting here rather than
  // relying on the scan order makes that guarantee explicit rather than incidental.
  seams.sort((a, b) => a.tile - b.tile);

  const openings = [
    [idx(4, 12), idx(7, 12)],
    [idx(7, 3), idx(4, 3)],
  ];

  // lastbatch: two of each type per side, at the gate mouths. Types are the indices in units.ts.
  const deploy0: Array<[number, number]> = [
    [0, idx(4, 12)],
    [0, idx(7, 12)],
    [1, idx(3, 13)],
    [1, idx(8, 13)],
    [2, idx(4, 11)],
    [2, idx(7, 11)],
  ];
  const deployment: Array<Array<[number, number]>> = [
    deploy0,
    deploy0.map(([u, t]) => [u, flipTile(t)] as [number, number]),
  ];

  const foundryTiles: number[] = [];
  for (let r = 7; r <= 8; r++) for (let c = 4; c <= 7; c++) foundryTiles.push(idx(c, r));

  cached = { terrain, forgeOf, cost, opaque, seams, forgeTiles, openings, deployment, foundryTiles };
  return cached;
}

export const passable = (m: MapData, t: number, seat: number): boolean =>
  m.terrain[t] !== Terrain.Ridge && (m.terrain[t] !== Terrain.Forge || m.forgeOf[t] === seat);

/**
 * Chebyshev distance in tiles. Seam capture uses the 3x3 block around a seam, which is exactly
 * "Chebyshev <= 1", and salvage uses the same shape — one rule, one helper, no chance of the two
 * drifting apart.
 */
export function chebyshev(a: number, b: number): number {
  const dc = Math.abs(colOf(a) - colOf(b));
  const dr = Math.abs(rowOf(a) - rowOf(b));
  return dc > dr ? dc : dr;
}

/**
 * Integer Bresenham between two tile centres, calling `blocked` for every tile strictly between
 * them. Returns true if the line is clear.
 *
 * This is the ONE line-of-fire implementation the game uses. tests/mechanism.test.ts deliberately
 * re-derives the same question with a completely different algorithm (a supercover grid walk using
 * floating-point parametric stepping) and compares — an audit that checks the game against its own
 * arithmetic is a tautology that passes on a completely broken game.
 */
export function lineClear(a: number, b: number, blocked: (tile: number) => boolean): boolean {
  // BOTH DIRECTIONS, AND BOTH MUST BE CLEAR. Integer Bresenham resolves an exact half-step tie
  // toward whichever end it started from, so the raw algorithm is direction-asymmetric: A can see B
  // while B cannot see A.
  //
  // The obvious fix — always trace from the lower tile index — buys reversal symmetry and QUIETLY
  // BREAKS ROTATIONAL SYMMETRY, which on this map matters more. Under the 180° point symmetry a
  // tile t maps to 191−t, so a pair that traced low-to-high traces high-to-low after rotation, and
  // the two seats get different answers about the same geometry. Requiring both traces to be clear
  // is symmetric under reversal by construction AND equivariant under rotation, because each
  // direction maps onto the corresponding direction. It costs one extra walk of at most eight
  // tiles. tests/mechanism.test.ts asserts both symmetries over all 192×192 pairs.
  return trace(a, b, blocked) && trace(b, a, blocked);
}

function trace(from: number, to: number, blocked: (tile: number) => boolean): boolean {
  let x0 = colOf(from);
  let y0 = rowOf(from);
  const x1 = colOf(to);
  const y1 = rowOf(to);
  const dx = Math.abs(x1 - x0);
  const dy = -Math.abs(y1 - y0);
  const sx = x0 < x1 ? 1 : -1;
  const sy = y0 < y1 ? 1 : -1;
  let err = dx + dy;
  for (;;) {
    if (x0 === x1 && y0 === y1) return true;
    const e2 = 2 * err;
    if (e2 >= dy) {
      err += dy;
      x0 += sx;
    }
    if (e2 <= dx) {
      err += dx;
      y0 += sy;
    }
    if (x0 === x1 && y0 === y1) return true;
    if (blocked(idx(x0, y0))) return false;
  }
}
